import { type ChatMessage, compileGptMessages, generateNanoId, dayjs, type ChatSession, type GPTChatMessage, type ChatModel, nextApiSendMessage, defaultModelPrismaToString, defaultModelGptLimit, type InputAPISend, nextApiAddMessages, convertGPTContentToChatContent, aiChatRequestHandler, getModelProvider, getModelConfig, availableTools, type ToolName, type ToolsConfig, supportsToolCalling, type OnMessageExtras, getMCPManager, DEFAULT_MODEL } from "@acme/util";
import { type Getter, type Setter } from "jotai";
import { chatUpdateTriggerAtom, chatSessionActiveAtom, chatSessionActiveIdAtom, chatAddMessageAtom, chatConversationTriggerAtom, chatUpdateMessageAtom, chatRemoveMessageAtom, playNotificationSound, chatGenerateTitleAtom, chatSessionsAtom, getAllApiKeys } from "../chat";
import { chatDefaultSettingsAtom, type AllApiKeys } from "../setting";
import { sendErrorHandlerAtom } from "../util";
import { globalToolsConfigAtom, globalAgentConfigAtom, activeAgentModeAtom, mcpServersConfigAtom } from "../instruction";

// =============================================================================
// TOOLS CONFIGURATION HELPERS
// =============================================================================

export interface ToolCallInfo {
    id: string;
    name: string;
    args: Record<string, unknown>;
    result?: unknown;
    error?: string;
    status: 'pending' | 'running' | 'completed' | 'error';
    startedAt: Date;
    completedAt?: Date;
    duration?: number;
}

/**
 * Build AI SDK tool set from tools configuration
 */
export const buildToolSet = (toolsConfig: ToolsConfig): Record<string, (typeof availableTools)[ToolName]> | undefined => {
    if (!toolsConfig.enabledTools || toolsConfig.enabledTools.length === 0) {
        return undefined;
    }

    const tools: Record<string, (typeof availableTools)[ToolName]> = {};
    for (const toolName of toolsConfig.enabledTools) {
        if (availableTools[toolName as ToolName]) {
            tools[toolName] = availableTools[toolName as ToolName];
        }
    }

    return Object.keys(tools).length > 0 ? tools : undefined;
};

type OnMessageCallback = (message: string, firstCall?: boolean, lastCall?: boolean, extras?: OnMessageExtras) => void;

export interface ToolsForMessage {
    tools?: Record<string, (typeof availableTools)[ToolName]>;
    toolChoice?: 'auto' | 'none' | 'required';
    maxSteps?: number;
    system?: string;
    onToolCall?: (toolName: string, args: unknown) => void;
    onToolResult?: (toolName: string, result: unknown) => void;
}

type ConfirmationRequiredResult = {
    requiresConfirmation: boolean;
    message?: string;
};

export const isConfirmationRequiredResult = (result: unknown): result is ConfirmationRequiredResult => {
    if (!result || typeof result !== 'object') return false;
    return (result as { requiresConfirmation?: unknown }).requiresConfirmation === true;
};

export const buildAgentSystemPrompt = (agentMode: 'chat' | 'agent'): string => {
    if (agentMode === 'agent') {
        return [
            'You are an autonomous agent assistant.',
            'When tools are available, proactively use them for facts, web data, calculations, API access, and verification instead of guessing.',
            'Plan briefly, execute tools step-by-step, then synthesize a clear final answer.',
            'For tools that require confirmation, ask for explicit user approval before executing and wait for the user response.',
            'Do not pretend a tool was used if it was not actually executed.',
        ].join(' ');
    }

    return [
        'When tools are available, use them whenever they improve accuracy.',
        'If a tool requires confirmation, ask user approval first and wait.',
        'Prefer real tool outputs over assumptions.',
    ].join(' ');
};

// =============================================================================
// MAIN MESSAGE HANDLER
// =============================================================================

export const handleSendMessageInit = async (
    get: Getter,
    set: Setter,
    session: ChatSession,
    message: ChatMessage,
    type: 'client' | 'cloud' | 'regenerate',
    isStreaming?: boolean
) => {
    const chatSessionId = session.id;
    set(chatConversationTriggerAtom, { scrollToBottom: true })

    if (message.senderRole !== 'user') return null;

    const modelFixed = message.model
        || session?.messages?.[0]?.model
        || session.defaultModel
        || DEFAULT_MODEL;

    const chatDefaultSettings = get(chatDefaultSettingsAtom)
    const defaultMaxTokensBySetting = (typeof chatDefaultSettings?.defaultMaxContextTokens === 'number' && chatDefaultSettings?.defaultMaxContextTokens)
        ? chatDefaultSettings?.defaultMaxContextTokens
        : defaultModelGptLimit(modelFixed).maxTokens

    const { newMessageFixed: newMessage, gptMessages, initInstruction } = compileGptMessages(session, message, defaultMaxTokensBySetting)

    // Update init instruction if present
    if (initInstruction) {
        if (type === 'cloud') {
            set(chatUpdateTriggerAtom, prev => ([...prev, { chatSessionId, update: { id: chatSessionId, defaultInstruction: initInstruction } }]))
        }
        set(chatSessionActiveAtom, { defaultInstruction: initInstruction })
    }
    set(chatAddMessageAtom, newMessage)

    const aiTempMessageId = generateNanoId(14)
    const abortController = new AbortController();

    // Get tools configuration
    const toolsConfig = get(globalToolsConfigAtom);
    const agentConfig = get(globalAgentConfigAtom);
    const agentMode = get(activeAgentModeAtom);

    // Track tool calls for display in UI
    const toolCallsCollected: ToolCallInfo[] = [];

    // Build tools if model supports it (built-in + MCP)
    let toolsForMessage: ToolsForMessage | undefined;
    const mcpServersUI = get(mcpServersConfigAtom);
    const hasBuiltinTools = toolsConfig.enabledTools.length > 0;
    const hasEnabledMCPServers = mcpServersUI.some(s => s.enabled);

    if (supportsToolCalling(modelFixed) && (hasBuiltinTools || hasEnabledMCPServers)) {
        // 1. Built-in tools
        const tools = hasBuiltinTools ? (buildToolSet(toolsConfig) ?? {}) : {};

        // 2. Connect MCP servers and merge their tools
        if (hasEnabledMCPServers) {
            try {
                const mcpManager = getMCPManager();
                const enabledServers = mcpServersUI.filter(s => s.enabled);
                // Connect to any servers not already connected
                for (const server of enabledServers) {
                    if (!mcpManager.isConnected(server.id)) {
                        await mcpManager.connect({
                            id: server.id,
                            name: server.name,
                            transport: { type: server.type, url: server.url },
                        });
                    }
                }
                // Merge MCP tools into the tool set
                const mcpTools = mcpManager.getAllTools();
                Object.assign(tools, mcpTools);
            } catch (mcpErr) {
                console.warn('⚠️ Failed to connect MCP servers:', mcpErr);
                // Continue with built-in tools only
            }
        }

        if (Object.keys(tools).length > 0) {
            toolsForMessage = {
                tools,
                toolChoice: 'auto',
                maxSteps: agentMode === 'agent' ? (agentConfig.maxSteps || 10) : 5,
                system: buildAgentSystemPrompt(agentMode),
                onToolCall: (toolName, args) => {
                    console.log(`🔧 Tool called: ${toolName}`, args);
                    const toolCallInfo: ToolCallInfo = {
                        id: generateNanoId(8),
                        name: toolName,
                        args: args as Record<string, unknown>,
                        status: 'running',
                        startedAt: new Date(),
                    };
                    toolCallsCollected.push(toolCallInfo);

                    // Update message to show tool is being called + current tool calls
                    set(chatUpdateMessageAtom, {
                        id: aiTempMessageId,
                        loadingText: `Using ${toolName}...`,
                        toolCalls: toolCallsCollected.map(tc => ({
                            id: tc.id,
                            name: tc.name,
                            args: tc.args,
                            status: tc.status,
                            result: tc.result,
                            error: tc.error,
                            duration: tc.duration,
                            startedAt: tc.startedAt,
                            completedAt: tc.completedAt,
                        })),
                    }, { chatSessionId: session?.id, isSkip: true });
                },
                onToolResult: (toolName, result) => {
                    console.log(`✅ Tool result: ${toolName}`, result);
                    const existingCall = toolCallsCollected.find(tc => tc.name === toolName && tc.status === 'running');
                    if (existingCall) {
                        existingCall.result = result;
                        if (isConfirmationRequiredResult(result)) {
                            existingCall.status = 'pending';
                            existingCall.error = result.message || 'Awaiting your approval to continue.';
                        } else {
                            existingCall.status = 'completed';
                            existingCall.error = undefined;
                        }
                        existingCall.completedAt = new Date();
                        existingCall.duration = existingCall.completedAt.getTime() - existingCall.startedAt.getTime();
                    }

                    // Update message with completed tool call
                    set(chatUpdateMessageAtom, {
                        id: aiTempMessageId,
                        ...(isConfirmationRequiredResult(result) ? { loadingText: 'Pending your approval...' } : {}),
                        toolCalls: toolCallsCollected.map(tc => ({
                            id: tc.id,
                            name: tc.name,
                            args: tc.args,
                            status: tc.status,
                            result: tc.result,
                            error: tc.error,
                            duration: tc.duration,
                            startedAt: tc.startedAt,
                            completedAt: tc.completedAt,
                        })),
                    }, { chatSessionId: session?.id, isSkip: true });
                },
            };
        }
    }

    const aiMessage: ChatMessage = {
        id: aiTempMessageId,
        chatSessionId: session.id,
        content: {
            contentType: 'text',
            content: '',
        },
        lastSync: dayjs().toDate(),
        userId: '',
        loading: true,
        parentId: newMessage.id,
        senderRole: 'assistant',
        created: dayjs().toDate(),
        model: modelFixed,
        abortSignal: abortController,
        loadingText: agentMode === 'agent'
            ? `Agent thinking with ${getModelConfig(modelFixed)?.displayName || modelFixed}...`
            : `Thinking with ${getModelConfig(modelFixed)?.displayName || modelFixed}...`
    }

    try {
        const allKeys = getAllApiKeys(get);
        const provider = getModelProvider(modelFixed);
        const modelSourceType = provider === 'anthropic' ? 'anthropic' : 'openai'

        if (type === 'client') {
            const onMessage = handleSendMessageInitClient(get, set, session, message, aiMessage, modelSourceType)
            await handleSendMessageFetchClient(get, set, session, newMessage, aiMessage, allKeys, gptMessages, modelFixed, abortController, onMessage, isStreaming, toolsForMessage)
        } else if (type === 'cloud') {
            const onMessage = handleSendMessageInitCloud(get, set, session, message, aiMessage, modelSourceType)
            await handleSendMessageFetchCloud(get, set, session, newMessage, aiMessage, allKeys, gptMessages, modelFixed, abortController, onMessage, isStreaming, undefined, undefined, toolsForMessage)
        }
        handleSendMessagePostFetch(get, set, session.id)

    } catch (error) {
        void handleSendMessageError(get, set, session, aiMessage.id, error, isStreaming, modelFixed)
    }

    handleSendMessageFinally(get, set, session, newMessage)
    return null
}


// =============================================================================
// INIT HANDLERS
// =============================================================================

export const handleSendMessageInitClient = (
    get: Getter,
    set: Setter,
    session: ChatSession,
    message: ChatMessage,
    aiMessage: ChatMessage,
    type: 'openai' | 'anthropic' = 'openai'
) => {
    set(chatAddMessageAtom, aiMessage, session.id)
    return handleSendMessageOnMessage(get, set, session, aiMessage, undefined, type)
}

export const handleSendMessageInitCloud = (
    get: Getter,
    set: Setter,
    session: ChatSession,
    message: ChatMessage,
    aiMessage: ChatMessage,
    type: 'openai' | 'anthropic' = 'openai'
) => {
    set(chatAddMessageAtom, aiMessage, session.id)

    return handleSendMessageOnMessage(
        get,
        set,
        session,
        aiMessage,
        {
            lastCall: async (combinedMessage: string, newAIMessage: ChatMessage, reasoning?: string) => {
                set(chatUpdateMessageAtom, {
                    id: newAIMessage.id,
                    abortSignal: null,
                    loading: false,
                    generating: false,
                    isThinking: false,
                    ...(reasoning ? { thinking: reasoning } : {}),
                }, { chatSessionId: session?.id, isSkip: true })

                await nextApiAddMessages({
                    messages: [{
                        ...newAIMessage,
                        content: {
                            contentType: 'text',
                            content: combinedMessage
                        },
                        ...(reasoning ? { thinking: reasoning } : {}),
                    }],
                    chatSessionId: session.id,
                })
            }
        },
        type
    )
}

export const handleSendMessageOnMessage = (
    get: Getter,
    set: Setter,
    session: ChatSession,
    aiMessage: ChatMessage,
    callbacks?: {
        lastCall?: (combinedMessage: string, newAIMessage: ChatMessage, reasoning?: string) => void | Promise<void>
    },
    _type: 'openai' | 'anthropic' = 'openai'
) => {
    // Remove is init flag
    if (session.isInit) set(chatSessionActiveAtom, { isInit: false });

    let messageTemp = ''
    let reasoningTemp = ''

    const onMessage = (message: string, firstCall?: boolean, lastCall?: boolean, extras?: OnMessageExtras) => {
        // Track reasoning content
        if (extras?.reasoning) {
            reasoningTemp = extras.reasoning;
        }

        if (firstCall) {
            set(chatUpdateMessageAtom, {
                id: aiMessage?.id,
                loading: false,
                generating: true,
                loadingText: undefined,
                // If we're in thinking phase, mark it
                ...(extras?.isThinking ? { isThinking: true } : {}),
            }, { chatSessionId: session?.id, isSkip: true })
        } else if (lastCall) {
            if (callbacks?.lastCall) {
                void callbacks.lastCall(messageTemp, aiMessage, reasoningTemp || undefined)
            } else {
                set(chatUpdateMessageAtom, {
                    id: aiMessage?.id,
                    loading: false,
                    generating: false,
                    isThinking: false,
                    ...(reasoningTemp ? { thinking: reasoningTemp } : {}),
                }, { chatSessionId: session?.id, isSkip: true })
            }
        }

        if (message === '[DONE]') {
            set(chatUpdateMessageAtom, {
                id: aiMessage?.id,
                loading: false,
                generating: false,
                isThinking: false,
            }, { chatSessionId: session?.id, isSkip: true })
            return
        }

        // AI SDK sends pre-accumulated content
        if (message !== '[DONE]' && message !== '') {
            messageTemp = message
            set(chatUpdateMessageAtom, {
                id: aiMessage.id,
                content: {
                    contentType: 'text',
                    content: message,
                },
                isThinking: false,
                ...(reasoningTemp ? { thinking: reasoningTemp } : {}),
            }, { chatSessionId: session.id, isSkip: true });
        } else if (extras?.isThinking && reasoningTemp) {
            // Still in thinking phase — no text yet, but update thinking content
            set(chatUpdateMessageAtom, {
                id: aiMessage.id,
                isThinking: true,
                thinking: reasoningTemp,
                loadingText: 'Thinking...',
            }, { chatSessionId: session.id, isSkip: true });
        }
    };

    return onMessage
}


// =============================================================================
// FETCH HANDLERS
// =============================================================================

export const handleSendMessageFetchClient = async (
    get: Getter,
    set: Setter,
    session: ChatSession,
    newMessage: ChatMessage,
    aiTempMessage: ChatMessage | undefined,
    apiKey: AllApiKeys,
    gptMessages: GPTChatMessage[],
    modelFixed: ChatModel,
    abortController: AbortController,
    onMessage?: OnMessageCallback,
    isStreaming?: boolean,
    toolsForMessage?: ToolsForMessage
) => {
    const gpt = await aiChatRequestHandler(
        apiKey,
        modelFixed,
        gptMessages,
        abortController,
        {
            ...(isStreaming ? { useStreaming: true } : {}),
            onMessage: onMessage,
            ...(toolsForMessage?.system ? { system: toolsForMessage.system } : {}),
            // Pass tools configuration
            ...(toolsForMessage && {
                tools: toolsForMessage.tools ? (Object.keys(toolsForMessage.tools) as ToolName[]) : undefined,
                toolChoice: toolsForMessage.toolChoice,
                maxSteps: toolsForMessage.maxSteps,
                onToolCall: toolsForMessage.onToolCall,
                onToolResult: toolsForMessage.onToolResult,
            }),
        }
    );

    if (!isStreaming) {
        aiTempMessage && set(chatRemoveMessageAtom, aiTempMessage.id, session.id)

        if (gpt?.choices) {
            for (const choice of gpt.choices) {
                const { content, contentCustoms } = convertGPTContentToChatContent(choice.message.content)
                const aiMessage: ChatMessage = {
                    id: generateNanoId(14),
                    chatSessionId: session.id,
                    content: content,
                    contentCustoms: contentCustoms,
                    userId: '',
                    senderRole: choice.message.role || 'assistant',
                    created: dayjs.unix(gpt.created).toDate(),
                    model: modelFixed,
                    parentId: newMessage.id,
                }
                set(chatAddMessageAtom, aiMessage, session.id)
            }
        }
    }
}

export const handleSendMessageFetchCloud = async (
    get: Getter,
    set: Setter,
    session: ChatSession,
    newMessage: ChatMessage,
    aiTempMessage: ChatMessage | undefined,
    apiKey: AllApiKeys,
    gptMessages: GPTChatMessage[],
    modelFixed: ChatModel,
    abortController: AbortController,
    onMessage?: OnMessageCallback,
    isStreaming?: boolean,
    apiParam?: Partial<InputAPISend>,
    extraParam?: { type?: 'regenerate' },
    toolsForMessage?: ToolsForMessage
) => {
    const chatSessionId = session.id
    const message = newMessage

    // Streaming with local API key
    if (isStreaming && apiKey) {
        if (extraParam?.type !== 'regenerate') {
            await nextApiAddMessages({
                chatSessionId: session.id,
                messages: [{
                    ...newMessage,
                    content: {
                        contentType: newMessage.content.contentType || 'text',
                        content: newMessage.content.content || ''
                    },
                }]
            });
        }
        await aiChatRequestHandler(
            apiKey,
            modelFixed,
            gptMessages,
            abortController,
            {
                ...(isStreaming ? { useStreaming: true } : {}),
                onMessage: onMessage,
                ...(toolsForMessage?.system ? { system: toolsForMessage.system } : {}),
                // Pass tools configuration
                ...(toolsForMessage && {
                    tools: toolsForMessage.tools ? (Object.keys(toolsForMessage.tools) as ToolName[]) : undefined,
                    toolChoice: toolsForMessage.toolChoice,
                    maxSteps: toolsForMessage.maxSteps,
                    onToolCall: toolsForMessage.onToolCall,
                    onToolResult: toolsForMessage.onToolResult,
                }),
            }
        );
    } else {
        // Non-streaming via API
        const newApiMessages = await nextApiSendMessage({
            prevMessages: gptMessages,
            defaultModel: modelFixed,
            apiKey,
            ...(toolsForMessage && {
                tools: toolsForMessage.tools ? Object.keys(toolsForMessage.tools) : undefined,
                toolChoice: toolsForMessage.toolChoice,
                maxSteps: toolsForMessage.maxSteps,
                system: toolsForMessage.system,
            }),
            message: {
                ...message,
                ...(message?.content && {
                    content: {
                        contentType: message.content.contentType || 'text',
                        content: message.content.content || ''
                    },
                    totalTokens: message.totalTokens || undefined,
                }),
                ...(message?.model && { model: message.model }),
            },
            ...apiParam,
        }, abortController);

        const newMessages: ChatMessage[] = newApiMessages.messages.map(a => ({
            ...a,
            parentId: a.parentId ?? undefined,
            replyToId: a.replyToId ?? undefined,
            userId: a.userId ?? undefined,
            model: a.model ? defaultModelPrismaToString(a.model) : undefined,
            content: a.content,
            contentCustoms: a.contentCustoms ?? undefined,
            totalTokens: a.totalTokens ?? undefined,
            updated: a.updated ?? undefined,
        }))

        set(chatRemoveMessageAtom, newMessage.id, chatSessionId, true)
        aiTempMessage && set(chatRemoveMessageAtom, aiTempMessage.id, chatSessionId, true)
        set(chatAddMessageAtom, newMessages, chatSessionId)
    }
}


// =============================================================================
// POST FETCH
// =============================================================================

export const handleSendMessagePostFetch = (
    get: Getter,
    set: Setter,
    sessionId?: string,
) => {
    playNotificationSound(get)
    // Only scroll if the completed session is still the active one
    const activeId = get(chatSessionActiveIdAtom);
    if (!sessionId || sessionId === activeId) {
        set(chatConversationTriggerAtom, { scrollToBottom: true })
    }
}


// =============================================================================
// ERROR HANDLING
// =============================================================================

export const handleSendMessageError = async (
    get: Getter,
    set: Setter,
    session: ChatSession,
    aiTempMessageId: string,
    error: Error | unknown,
    isStreaming?: boolean,
    model?: ChatModel
) => {
    const err = error as Error;
    const errorMessage = formatErrorMessage(err, model);

    const isCloud = session?.isSync && !session?.isInit

    if (!isStreaming) {
        // Non-streaming: remove temp message and show error
        set(chatRemoveMessageAtom, aiTempMessageId, session.id, true)
    } else {
        // Streaming: update message with error
        set(chatUpdateMessageAtom, {
            id: aiTempMessageId,
            loading: false,
            generating: false,
            error: errorMessage,
            content: {
                contentType: 'text',
                content: `⚠️ Error: ${errorMessage}`
            }
        }, { chatSessionId: session?.id, isSkip: true })

        // If cloud sync, still save the message with error
        if (isCloud) {
            const findSession = get(chatSessionsAtom).find(a => a.id === session.id)
            const findMessage = findSession?.messages.find(a => a.id === aiTempMessageId)
            if (findMessage) {
                await nextApiAddMessages({
                    messages: [{
                        ...findMessage,
                        content: {
                            contentType: 'text',
                            content: findMessage.content.content || ''
                        }
                    }],
                    chatSessionId: session.id,
                })
            }
        }
    }

    set(sendErrorHandlerAtom, { error })
}

/**
 * Format error messages for user-friendly display
 */
function formatErrorMessage(error: Error | unknown, model?: ChatModel): string {
    const err = error as Error;
    const message = err?.message || 'Unknown error occurred';
    const modelConfig = model ? getModelConfig(model) : null;
    const modelName = modelConfig?.displayName || model || 'the model';
    const lowercaseMessage = message.toLowerCase();

    // Rate limit errors
    if (lowercaseMessage.includes('rate limit') || message.includes('429') || lowercaseMessage.includes('too many requests') || lowercaseMessage.includes('quota exceeded')) {
        return 'Rate limit exceeded. Please wait a moment and try again, or consider upgrading your API plan.';
    }

    // Context length / token errors
    if (lowercaseMessage.includes('context') || lowercaseMessage.includes('tokens') || lowercaseMessage.includes('too long') || lowercaseMessage.includes('maximum context length') || lowercaseMessage.includes('context_length_exceeded')) {
        return `Message too long for ${modelName}. Try using a shorter message or clearing some chat history.`;
    }

    // API key errors
    if (lowercaseMessage.includes('api key') || lowercaseMessage.includes('authentication') || message.includes('401') || lowercaseMessage.includes('invalid api') || lowercaseMessage.includes('incorrect api') || lowercaseMessage.includes('unauthorized')) {
        return 'Invalid API key. Please check your API key in settings.';
    }

    // Missing API key
    if (lowercaseMessage.includes('no api key') || lowercaseMessage.includes('api key is required') || lowercaseMessage.includes('please add your api')) {
        return 'API key not configured. Please add your API key in settings.';
    }

    // Model not found / access denied
    if (lowercaseMessage.includes('does not exist') || lowercaseMessage.includes('not found') || message.includes('404') || lowercaseMessage.includes('model_not_found')) {
        return `${modelName} is not available. Make sure you have access to this model.`;
    }

    // Model access / permission errors
    if (lowercaseMessage.includes('access denied') || lowercaseMessage.includes('permission denied') || lowercaseMessage.includes('insufficient_quota') || lowercaseMessage.includes('billing')) {
        return `Access denied for ${modelName}. Check your API subscription and billing status.`;
    }

    // Network errors
    if (lowercaseMessage.includes('network') || lowercaseMessage.includes('fetch failed') || lowercaseMessage.includes('econnrefused') || lowercaseMessage.includes('enotfound') || lowercaseMessage.includes('dns')) {
        return 'Network error. Please check your internet connection and try again.';
    }

    // Timeout errors
    if (lowercaseMessage.includes('timeout') || lowercaseMessage.includes('etimedout') || lowercaseMessage.includes('timed out')) {
        return 'Request timed out. The AI service is taking too long. Please try again.';
    }

    // Abort/Cancel errors
    if (lowercaseMessage.includes('abort') || lowercaseMessage.includes('cancelled') || lowercaseMessage.includes('canceled')) {
        return 'Request was cancelled.';
    }

    // Server errors
    if (message.includes('500') || message.includes('502') || message.includes('503') || message.includes('504') || lowercaseMessage.includes('internal server error') || lowercaseMessage.includes('bad gateway') || lowercaseMessage.includes('service unavailable')) {
        return 'Server error. The AI service is temporarily unavailable. Please try again in a few minutes.';
    }

    // Content filter / safety errors
    if ((lowercaseMessage.includes('content') && lowercaseMessage.includes('filter')) || lowercaseMessage.includes('content_policy') || lowercaseMessage.includes('safety') || lowercaseMessage.includes('flagged')) {
        return 'Content was filtered by the AI safety system. Please rephrase your message.';
    }

    // Overloaded / capacity errors
    if (lowercaseMessage.includes('overloaded') || lowercaseMessage.includes('capacity') || lowercaseMessage.includes('busy')) {
        return 'The AI service is currently overloaded. Please try again in a moment.';
    }

    // Stream errors
    if (lowercaseMessage.includes('stream') && (lowercaseMessage.includes('error') || lowercaseMessage.includes('failed'))) {
        return 'Failed to receive response. Please try again.';
    }

    // JSON parse errors (usually from malformed responses)
    if (lowercaseMessage.includes('json') || lowercaseMessage.includes('parse') || lowercaseMessage.includes('unexpected token')) {
        return 'Received an invalid response from the AI service. Please try again.';
    }

    // Default: return cleaned message (but make it friendlier)
    const cleanedMessage = message.replace(/^Error:\s*/i, '').trim();

    // If the message is too technical, provide a generic one
    if (cleanedMessage.length > 200 || cleanedMessage.includes('stack') || cleanedMessage.includes('at ')) {
        return 'Something went wrong. Please try again or contact support if the issue persists.';
    }

    return cleanedMessage;
}


// =============================================================================
// FINALIZE
// =============================================================================

const handleSendMessageFinally = (
    get: Getter,
    set: Setter,
    session: ChatSession,
    newMessage: ChatMessage,
) => {
    // Generate title for new chats
    if (session.name === 'New Chat' && (session.messages.length || 0) <= 1) {
        void set(chatGenerateTitleAtom, session.id, newMessage)
    }
}
